import * as Sentry from '@sentry/react'
import FlagProvider from '@unleash/proxy-client-react'
import 'react-app-polyfill/ie9'
import 'react-app-polyfill/stable'
import { createRoot } from 'react-dom/client'
import { Provider as ReduxProvider } from 'react-redux'
import { BrowserRouter } from 'react-router-dom'

import { ScrollToTop } from './routes/ScrollToTop'
import { store } from './store'

import { flagsConfig } from './services/flags'
import './services/translation'

import { MetaHead } from './components/MetaHead/MetaHead'
import GlobalStyle from './components/styles/GlobalStyle'
import { Auth } from './containers/auth'
import { config } from './utils/helpers/config.helpers'

import 'react-loading-skeleton/dist/skeleton.css'
import './index.css'

import { initOry } from '@web-apps/feature-auth'
import { SuspenseWrapper } from '@web-apps/ui-shared'
import {
  initAPI,
  initConfig,
  markEmbeddedRuntime,
} from '@web-apps/utils-shared'
import { MaintenanceWrapper, ToastsWrapper } from './components'
import { RNWebViewAuthWrapper } from './components/RNWebViewAuthWrapper'
import * as serviceWorker from './serviceWorker'
import {
  IMPERSONATED_ZEZAM_USER_QUERY_PARAM,
  setImpersonatedZezamId,
} from './utils/helpers/impersonation.helpers'
import {
  initRNWebViewAuthListener,
  RN_WEBVIEW_SESSION_STORAGE_KEY,
  RN_WEBVIEW_URL_PARAM,
} from './utils/helpers/rnWebView.helpers'

const searchParams = new URLSearchParams(window.location.search)

const asZezamUserId = searchParams.get(IMPERSONATED_ZEZAM_USER_QUERY_PARAM)
const isRNWebView =
  searchParams.has(RN_WEBVIEW_URL_PARAM) ||
  sessionStorage.getItem(RN_WEBVIEW_SESSION_STORAGE_KEY) === 'true'

if (isRNWebView) {
  sessionStorage.setItem(RN_WEBVIEW_SESSION_STORAGE_KEY, 'true')
  markEmbeddedRuntime()
  initRNWebViewAuthListener()
}

if (asZezamUserId) {
  if (!isRNWebView) setImpersonatedZezamId(asZezamUserId)

  searchParams.delete(IMPERSONATED_ZEZAM_USER_QUERY_PARAM)
  const newUrl =
    window.location.pathname +
    (searchParams.toString() ? '?' + searchParams.toString() : '') +
    window.location.hash
  window.history.replaceState({}, '', newUrl)
}

if (process.env.NODE_ENV === 'production') {
  Sentry.init({
    dsn: config.sentry.dsn,
    release: process.env.REACT_APP_VERSION,
    environment: config.sentry.environment,
    sampleRate: config.sentry.errorsSampleRate,
    tracesSampleRate: config.sentry.tracesSampleRate,

    // The list of errors was based on a suggestion here: https://docs.sentry.io/platforms/javascript/configuration/filtering/#decluttering-sentry
    ignoreErrors: [
      // TBU based on fantom errors
      'Network Error',
      // https://github.com/getsentry/sentry-javascript/issues/7993
      'webkit-masked-url',
    ],
    denyUrls: [
      // Facebook flakiness
      /graph\.facebook\.com/i,
      // Facebook blocked
      /connect\.facebook\.net\/en_US\/all\.js/i,
      // Chrome extensions
      /chrome-extension/i,
      /extensions\//i,
      /^chrome:\/\//i,
      /C:\\Users/i,
    ],
  })
}

if (config.apiBaseUrl) initAPI({ baseURL: config.apiBaseUrl })

const root = createRoot(document.getElementById('root') as HTMLElement)

root.render(
  <FlagProvider config={flagsConfig}>
    <ReduxProvider store={store}>
      <SuspenseWrapper>
        <MaintenanceWrapper>
          <MetaHead />
          <GlobalStyle />
          <BrowserRouter>
            <ScrollToTop />
            <RNWebViewAuthWrapper isNativeAuthRequired={isRNWebView}>
              <Auth />
            </RNWebViewAuthWrapper>
            <ToastsWrapper />
          </BrowserRouter>
        </MaintenanceWrapper>
      </SuspenseWrapper>
    </ReduxProvider>
  </FlagProvider>
)

if (config.iframelyKey && config.chargebeeSite && config.authUrl) {
  initConfig({
    IFRAMELY_KEY: config.iframelyKey,
    CHARGEBEE_SITE: config.chargebeeSite,
    AUTH_URL: config.authUrl,
    CREATOR_APP_BASE_URL: config.creatorAppBaseUrl,
    PUBLIC_APP_BASE_URL: config.publicAppBaseUrl,
    FACEBOOK_APP_ID: config.facebookAppId,
  })
  initOry()
}

serviceWorker.register()
